import { PlayerInput } from '@push.io/shared';
import { directionFromPoints, normalizeDirection } from './input-math.js';

type InputCallback = (input: PlayerInput) => void;
type InputDevice = 'keyboard' | 'mouse' | 'touch';
export type ControlScheme = 'wasd' | 'mouse' | 'touch';

const AIM_DEAD_ZONE = 8;
const TOUCH_RADIUS = 72;
const SMOOTHING = 0.24;

export class InputHandler {
  private readonly element: HTMLElement;
  private readonly keys = new Set<string>();
  private sequence = 1;
  private callback: InputCallback | null = null;
  private activeDevice: InputDevice = 'keyboard';
  private cursorX = 0;
  private cursorY = 0;
  private mouseReady = false;
  private touchId: number | null = null;
  private touchOriginX = 0;
  private touchOriginY = 0;
  private targetX = 0;
  private targetY = 0;
  private smoothedX = 0;
  private smoothedY = 0;
  private lastTouchAt = 0;
  private controlScheme: ControlScheme = 'wasd';
  private enabled = true;
  private mouseSensitivity = 1;

  constructor(element: HTMLElement) {
    this.element = element;
    this.element.style.touchAction = 'none';
    this.element.style.userSelect = 'none';
    this.setupListeners();
  }

  private setupListeners(): void {
    window.addEventListener('keydown', (event) => {
      if (!this.enabled || this.isInteractiveTarget(event.target)) return;
      if (this.controlScheme !== 'wasd') return;
      if (!this.isMovementKey(event.key)) return;
      event.preventDefault();
      this.activeDevice = 'keyboard';
      this.keys.add(event.key.toLowerCase());
      this.updateKeyboardTarget();
    });
    window.addEventListener('keyup', (event) => {
      if (!this.isMovementKey(event.key)) return;
      if (!this.enabled || this.isInteractiveTarget(event.target)) {
        this.keys.delete(event.key.toLowerCase());
        this.updateKeyboardTarget();
        return;
      }
      event.preventDefault();
      this.keys.delete(event.key.toLowerCase());
      this.updateKeyboardTarget();
    });
    window.addEventListener('pointermove', (event) => this.handlePointerMove(event), { passive: false, capture: true });
    window.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { passive: false, capture: true });
    window.addEventListener('pointerup', (event) => this.handlePointerEnd(event), { passive: false, capture: true });
    window.addEventListener('pointercancel', (event) => this.handlePointerEnd(event), { passive: false, capture: true });
    window.addEventListener('blur', () => this.reset());
    window.addEventListener('resize', () => {
      if (this.activeDevice === 'mouse') this.updateMouseTarget();
    });
    window.addEventListener('contextmenu', (event) => {
      if (this.enabled && !this.isInteractiveTarget(event.target)) event.preventDefault();
    });
  }

  private isMovementKey(key: string): boolean {
    return ['w', 'a', 's', 'd', 'arrowup', 'arrowleft', 'arrowdown', 'arrowright'].includes(key.toLowerCase());
  }

  private handlePointerDown(event: PointerEvent): void {
    if (!this.enabled || event.pointerType !== 'touch') return;
    if (this.controlScheme !== 'touch') return;
    if (this.isInteractiveTarget(event.target) || this.touchId !== null) return;
    event.preventDefault();
    this.lastTouchAt = performance.now();
    this.activeDevice = 'touch';
    this.touchId = event.pointerId;
    this.touchOriginX = event.clientX;
    this.touchOriginY = event.clientY;
    this.cursorX = event.clientX;
    this.cursorY = event.clientY;
    this.targetX = 0;
    this.targetY = 0;
  }

  private handlePointerMove(event: PointerEvent): void {
    if (!this.enabled || this.isInteractiveTarget(event.target)) return;
    if (event.pointerType === 'touch') {
      if (event.pointerId !== this.touchId) return;
      event.preventDefault();
      this.lastTouchAt = performance.now();
      this.activeDevice = 'touch';
      this.cursorX = event.clientX;
      this.cursorY = event.clientY;
      this.updateTouchTarget(event.clientX, event.clientY);
      return;
    }
    if (this.controlScheme !== 'mouse') return;
    if (performance.now() - this.lastTouchAt < 500) return;
    this.activeDevice = 'mouse';
    this.cursorX = event.clientX;
    this.cursorY = event.clientY;
    this.mouseReady = true;
    this.updateMouseTarget();
  }

  private handlePointerEnd(event: PointerEvent): void {
    if (event.pointerType !== 'touch' || event.pointerId !== this.touchId) return;
    event.preventDefault();
    this.lastTouchAt = performance.now();
    this.touchId = null;
    this.targetX = 0;
    this.targetY = 0;
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    return target instanceof Element
      && !!target.closest('button, select, input, textarea, a, [contenteditable="true"], [data-game-input="ignore"]');
  }

  private updateKeyboardTarget(): void {
    let x = 0;
    let y = 0;
    if (this.keys.has('w') || this.keys.has('arrowup')) y -= 1;
    if (this.keys.has('s') || this.keys.has('arrowdown')) y += 1;
    if (this.keys.has('a') || this.keys.has('arrowleft')) x -= 1;
    if (this.keys.has('d') || this.keys.has('arrowright')) x += 1;
    const direction = normalizeDirection(x, y);
    this.targetX = direction.x;
    this.targetY = direction.y;
  }

  private updateMouseTarget(): void {
    if (!this.mouseReady) {
      this.targetX = 0;
      this.targetY = 0;
      return;
    }
    const rect = this.element.getBoundingClientRect();
    const direction = directionFromPoints(
      rect.left + rect.width / 2,
      rect.top + rect.height / 2,
      this.cursorX,
      this.cursorY,
      AIM_DEAD_ZONE,
      AIM_DEAD_ZONE + 1,
    );
    this.targetX = direction.x;
    this.targetY = direction.y;
  }

  private updateTouchTarget(x: number, y: number): void {
    const direction = directionFromPoints(
      this.touchOriginX,
      this.touchOriginY,
      x,
      y,
      AIM_DEAD_ZONE,
      TOUCH_RADIUS,
    );
    this.targetX = direction.x;
    this.targetY = direction.y;
  }

  private createInput(): PlayerInput {
    return {
      sequence: this.sequence++,
      timestamp: Date.now(),
      dirX: this.smoothedX,
      dirY: this.smoothedY,
      inputMode: this.activeDevice === 'keyboard' ? 'wasd' : this.activeDevice,
    };
  }

  onInput(callback: InputCallback): void {
    this.callback = callback;
  }

  setControlScheme(scheme: ControlScheme): void {
    this.controlScheme = scheme;
    this.reset();
    if (scheme === 'wasd') this.activeDevice = 'keyboard';
    if (scheme === 'mouse') this.activeDevice = 'mouse';
    if (scheme === 'touch') this.activeDevice = 'touch';
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.reset();
  }

  setMouseSensitivity(value: number): void {
    this.mouseSensitivity = Math.max(0.5, Math.min(2, value));
  }

  private reset(): void {
    this.keys.clear();
    this.touchId = null;
    this.targetX = 0;
    this.targetY = 0;
    this.smoothedX = 0;
    this.smoothedY = 0;
  }

  update(_localX?: number, _localY?: number): void {
    void _localX;
    void _localY;
    if (!this.callback || !this.enabled) return;
    if (this.activeDevice === 'keyboard') this.updateKeyboardTarget();
    if (this.activeDevice === 'mouse') this.updateMouseTarget();
    const smoothing = this.activeDevice === 'mouse'
      ? 1 - Math.pow(1 - SMOOTHING, this.mouseSensitivity)
      : SMOOTHING;
    this.smoothedX += (this.targetX - this.smoothedX) * smoothing;
    this.smoothedY += (this.targetY - this.smoothedY) * smoothing;
    if (Math.abs(this.smoothedX) < 0.001) this.smoothedX = 0;
    if (Math.abs(this.smoothedY) < 0.001) this.smoothedY = 0;
    this.callback(this.createInput());
  }
}
